import { Button, Input } from '@breetometer/components';
import { Beneficiaries, FiatInput, NoBeneficiary } from 'BreetComponents';
import {
	checkNearestValidAmount,
	type CURRENCYCODETYPE,
	formatCurrencyInputAmount,
	formatNumberWithCommas,
	getCleanedValue,
	getNearestValidAmount,
	getWithdrawRules,
	handleDecimalPoints,
	type IBeneficiary,
} from 'BreetHelpers';
import { useFiatWithdrawRequestContext, useWithdrawRequestContext } from 'BreetHooks';
import { selectBeneficiaries, selectFiatMeta, useAppSelector } from 'BreetRedux';
import { type ChangeEvent, useCallback, useState } from 'react';

const FormComponent = () => {
	const { userWalletProps, withdrawRequestHookForm, meta, formStage } = useFiatWithdrawRequestContext();

	const { selectedWallet } = useWithdrawRequestContext();

	const currencyCode = userWalletProps.code as unknown as CURRENCYCODETYPE;

	const fiatMeta = useAppSelector(selectFiatMeta);

	const beneficiaries = useAppSelector(selectBeneficiaries);

	const { withdrawalMaxAmount, withdrawalMinAmount } = getWithdrawRules(meta?.settings, currencyCode);

	const [inputMessage, setInputMessage] = useState('');

	const {
		setError,
		clearErrors,
		watch,
		setValue,
		formState: { errors },
		register,
		handleSubmit,
	} = withdrawRequestHookForm;

	const [inputValue, setInputValue] = useState<string>(() => formatCurrencyInputAmount(watch('amount'), currencyCode));

	const validateAmount = useCallback(
		(cleanedValue: string) => {
			const numericAmount = Number(cleanedValue.replace(/,/g, ''));

			if (numericAmount > userWalletProps.walletBalance) {
				setError('amount', {
					message: 'Insufficient balance',
				});
				return;
			}

			if (numericAmount < withdrawalMinAmount) {
				setError('amount', {
					message: `The minimum withdrawal amount is ${userWalletProps.symbol} ${formatNumberWithCommas(withdrawalMinAmount)}`,
				});
				return;
			}

			if (!!withdrawalMaxAmount && numericAmount > withdrawalMaxAmount) {
				setError('amount', {
					message: `The maximum withdrawal amount is ${userWalletProps.symbol} ${formatNumberWithCommas(withdrawalMaxAmount)}`,
				});
				return;
			}

			const nearestValidAmountError = checkNearestValidAmount(numericAmount, currencyCode);

			if (nearestValidAmountError) {
				setError('amount', { message: nearestValidAmountError });
				return;
			}
			clearErrors('amount');
		},
		[
			userWalletProps.walletBalance,
			withdrawalMinAmount,
			withdrawalMaxAmount,
			currencyCode,
			userWalletProps.symbol,
			clearErrors,
			setError,
		]
	);

	const handleAmountChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const { value } = event.target;

			if (!!errors.amount?.message || !value) clearErrors('amount');

			let cleanedValue = getCleanedValue(value);

			cleanedValue = handleDecimalPoints(cleanedValue);

			setInputValue(formatCurrencyInputAmount(cleanedValue, currencyCode));

			setValue('amount', cleanedValue, { shouldDirty: true });

			validateAmount(cleanedValue);

			if (inputMessage) {
				setInputMessage('');
			}
		},
		[errors.amount?.message, clearErrors, currencyCode, setValue, validateAmount, inputMessage]
	);

	const handleMaxClick = useCallback(() => {
		const walletBalance = userWalletProps.walletBalance;

		let amount = Math.min(walletBalance, withdrawalMaxAmount);

		if (inputMessage) {
			setInputMessage('');
		}

		if (walletBalance > withdrawalMaxAmount) {
			setInputMessage(
				`Maximum withdrawal per transaction is ${userWalletProps.symbol} ${formatNumberWithCommas(withdrawalMaxAmount)}`
			);
		}

		amount = getNearestValidAmount(amount, selectedWallet?.currency === 'NGN');

		let cleanedValue = getCleanedValue(amount.toString());

		cleanedValue = handleDecimalPoints(cleanedValue);

		setInputValue(formatCurrencyInputAmount(cleanedValue, currencyCode));

		setValue('amount', cleanedValue, { shouldDirty: true });

		validateAmount(cleanedValue);
	}, [
		userWalletProps.walletBalance,
		userWalletProps.symbol,
		withdrawalMaxAmount,
		inputMessage,
		selectedWallet?.currency,
		currencyCode,
		setValue,
		validateAmount,
	]);

	const selectBeneficiary = useCallback(
		(beneficiary: IBeneficiary | undefined) => {
			if (!beneficiary) return;

			setValue('selectedBeneficiary', {
				accountName: beneficiary.accountName,
				accountNumber: beneficiary.accountNumber,
				bankName: beneficiary.bankName,
				id: beneficiary._id,
			});

			setValue('narration', beneficiary.narration ?? '');
		},
		[setValue]
	);

	const isFormValid = !errors.amount?.message && !!watch('selectedBeneficiary.id') && !!watch('amount');

	return (
		<form
			onSubmit={handleSubmit(formStage.navigator.goTo('withdraw-details'))}
			className='payment-request-form withdraw-request-form'
			data-testid='withdraw-form'
			noValidate
		>
			<FiatInput.WithdrawRequest
				label='Enter Amount'
				currencyCode={selectedWallet?.currency ?? fiatMeta.currency}
				handleMaxClick={handleMaxClick}
				onChange={handleAmountChange}
				errorMessage={errors.amount?.message}
				message={inputMessage}
				value={inputValue}
			/>

			{!!beneficiaries.length && (
				<Beneficiaries
					onAddNewBeneficiary={formStage.navigator.goTo('add-beneficiary')}
					selectBeneficiary={selectBeneficiary}
					selectedBeneficiaryId={watch('selectedBeneficiary').id}
					title='Select Destination'
					onManageBeneficiaries={formStage.navigator.goTo('manage-beneficiaries')}
				/>
			)}

			<div className='narration-input'>
				<Input
					id='narration'
					label='Narration'
					className='bg-primary-500 border-dark-navy'
					widthType='full'
					{...register('narration')}
					errorMessage={errors.narration?.message}
					required
				/>
			</div>

			{!beneficiaries.length && (
				<NoBeneficiary
					intent='initiate a withdrawal'
					header
					addAccount={formStage.navigator.goTo('add-beneficiary')}
				/>
			)}

			<div className='payment-request-form_cta'>
				<Button
					label='Next'
					type='submit'
					className='right-sheet_cta_fullwidth'
					disabled={!isFormValid}
				/>
			</div>
		</form>
	);
};

export default FormComponent;
